import { useEffect, useState } from 'react'
import { featureRequestApi, type PublicFeatureRequest } from '../services/api'

/**
 * Public list of approved feature requests rendered below the request CTA
 * on /roadmap. Renders NOTHING when the list is empty (per design pass 2 —
 * an empty "no requests yet" state is worse than no section at all on a
 * proof-of-shipping page).
 *
 * Layout is a vertical changelog-style log, not a card grid, to differentiate
 * from the phases grid above and match the existing roadmap aesthetic.
 *
 * Upvote state is optimistic: click → flip locally + count ±1 → fire request →
 * on error, revert and surface a small inline error. No toast lib needed.
 */
export default function CommunityRequests() {
  const [items, setItems] = useState<PublicFeatureRequest[] | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [voteError, setVoteError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    featureRequestApi
      .listApproved()
      .then((res) => {
        if (cancelled) return
        setItems(res.data || [])
      })
      .catch(() => {
        if (cancelled) return
        setLoadError(true)
        setItems([])
      })
    return () => { cancelled = true }
  }, [])

  // Don't render the section at all until we know there's something to show.
  // Loading flash is fine on a static-ish page; an empty state would lie about
  // the value of the section.
  if (items === null) return null
  if (items.length === 0) return null

  const toggle = async (item: PublicFeatureRequest) => {
    setVoteError(null)
    const wasVoted = item.has_voted
    const nextVoted = !wasVoted
    const optimistic = items.map((it) =>
      it.id === item.id
        ? { ...it, has_voted: nextVoted, upvotes: it.upvotes + (nextVoted ? 1 : -1) }
        : it,
    )
    setItems(optimistic)
    try {
      const res = wasVoted
        ? await featureRequestApi.removeUpvote(item.id)
        : await featureRequestApi.upvote(item.id)
      // Snap to the server count in case our optimistic delta drifted.
      setItems((prev) => prev && prev.map((it) =>
        it.id === item.id ? { ...it, has_voted: res.has_voted, upvotes: res.upvotes } : it,
      ))
    } catch (err) {
      // Revert.
      setItems((prev) => prev && prev.map((it) =>
        it.id === item.id ? { ...it, has_voted: wasVoted, upvotes: item.upvotes } : it,
      ))
      setVoteError(err instanceof Error ? err.message : 'Vote failed')
    }
  }

  return (
    <section className="od-roadmap-community" aria-labelledby="roadmap-community-title">
      <header className="od-roadmap-community-head">
        <p className="od-section-label od-section-label-plus">you asked. we built it.</p>
        <h2 id="roadmap-community-title">Shipped from your ideas</h2>
        {loadError && (
          <p className="od-roadmap-community-loaderror">
            Could not load community requests. Refresh to retry.
          </p>
        )}
      </header>

      <ol className="od-roadmap-community-list">
        {items.map((item) => (
          <li key={item.id} className={`od-roadmap-community-row od-roadmap-community-row-${item.phase || 'now'}`}>
            <span className="od-roadmap-community-dot" aria-hidden="true" />
            <time className="od-roadmap-community-date" dateTime={item.approved_at}>
              {formatShortDate(item.approved_at)}
            </time>
            <div className="od-roadmap-community-body">
              <h3 className="od-roadmap-community-title">{item.title}</h3>
              <p className="od-roadmap-community-desc">{item.description}</p>
            </div>
            <button
              type="button"
              className={`od-roadmap-community-vote${item.has_voted ? ' od-roadmap-community-vote-active' : ''}`}
              onClick={() => toggle(item)}
              aria-pressed={item.has_voted}
              aria-label={`${item.has_voted ? 'Remove your upvote' : 'Upvote'} (${item.upvotes})`}
            >
              <span className="od-roadmap-community-vote-arrow" aria-hidden="true">▲</span>
              <span className="od-roadmap-community-vote-count">{item.upvotes}</span>
            </button>
          </li>
        ))}
      </ol>

      {voteError && (
        <p className="od-roadmap-community-voteerror" role="alert">{voteError}</p>
      )}
    </section>
  )
}

function formatShortDate(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
